/**
 * @file     mapboxMapConstants.ts
 * @purpose  Pure module-scope constants for MapboxNeighborhoodMap: accent, the
 *           default camera, municipality name maps, TransLink line colors, base
 *           labels to hide, the static paint expressions (module-scope so their
 *           referential identity is stable — Mapbox recompiles shaders when a
 *           paint object's identity changes), and overlay disclaimers.
 */
import type { ExpressionSpecification } from 'mapbox-gl';
import { colorPalettesByMode } from '@/design-system/tokens/colors';

export const ACCENT = '#4DB2BA';
/** ACCENT is 2.5:1 on a light surface. Glyphs and text use the strong accent
 *  (5.9:1 light, theme-aware via the CSS var); white text sits on the strong
 *  fill, which is dark enough in both themes. */
export const ACCENT_INK = 'var(--r-accent-strong)';
export const ACCENT_STRONG_FILL = colorPalettesByMode.light.accent[700];
export const INITIAL_VIEW = { longitude: -122.92, latitude: 49.20, zoom: 9.3 };

// Province-wide bounding box (matches the map's maxBounds minus the pan
// buffer). Used to scope street geocoding when zoomed out and to accept
// user geolocation anywhere in BC.
export const BC_BBOX = { west: -139, east: -114, south: 48, north: 60 } as const;

export function isInBcBbox(lng: number, lat: number): boolean {
  return lat > BC_BBOX.south && lat < BC_BBOX.north && lng > BC_BBOX.west && lng < BC_BBOX.east;
}

// Single source of truth for the runtime-injected GL stylesheet (the map
// component injects it on mount; pages that defer the GL mount preload it).
export const MAPBOX_GL_CSS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.22.0/mapbox-gl.css';

export const BC_NAME_TO_KEY: Record<string, string> = {
  'City of Vancouver': 'Vancouver', 'District Municipality of West Vancouver': 'West Van',
  'The Corporation of the District of North Vancouver': 'North Van District',
  'City of North Vancouver': 'North Van City', 'City of Burnaby': 'Burnaby',
  'City of Richmond': 'Richmond', 'City of Surrey': 'Surrey',
  'City of Coquitlam': 'Coquitlam', 'City of Port Coquitlam': 'Port Coquitlam',
  'City of Port Moody': 'Port Moody', 'City of New Westminster': 'New Westminster',
  'City of Delta': 'Delta', 'City of Pitt Meadows': 'Pitt Meadows',
  'City of Maple Ridge': 'Maple Ridge', 'City of Langley': 'Langley city',
  'The Corporation of the Township of Langley': 'Langley district',
  'Bowen Island Municipality': 'Bowen Island', 'Village of Lions Bay': 'Lions Bay',
  'Village of Anmore': 'Anmore', 'Village of Belcarra': 'Belcarra',
  'City of White Rock': 'White Rock', 'University Endowment Lands': 'UBC',
};

export const SHORT_NAMES: Record<string, string> = {
  'City of Vancouver': 'Vancouver', 'District Municipality of West Vancouver': 'West Van',
  'The Corporation of the District of North Vancouver': 'N. Van District',
  'City of North Vancouver': 'N. Van City', 'City of Burnaby': 'Burnaby',
  'City of Richmond': 'Richmond', 'City of Surrey': 'Surrey',
  'City of Coquitlam': 'Coquitlam', 'City of Port Coquitlam': 'Port Coquitlam',
  'City of Port Moody': 'Port Moody', 'City of New Westminster': 'New West',
  'City of Delta': 'Delta', 'City of Pitt Meadows': 'Pitt Meadows',
  'City of Maple Ridge': 'Maple Ridge', 'City of Langley': 'Langley City',
  'The Corporation of the Township of Langley': 'Langley Twp',
  'Bowen Island Municipality': 'Bowen Island', 'Village of Lions Bay': 'Lions Bay',
  'Village of Anmore': 'Anmore', 'Village of Belcarra': 'Belcarra',
  'City of White Rock': 'White Rock', 'University Endowment Lands': 'UBC',
};

// Official TransLink line colors (plus Evergreen retained for back-compat with older data)
export const LINE_COLORS: Record<string, string> = {
  expo: '#0033A0',
  millennium: '#FFCD00',
  canada: '#007C9F',
  // Bridgeport → Richmond-Brighouse branch: same family, one step lighter so the
  // two Canada Line branches read as distinct without looking like another line.
  'canada-richmond': '#3FA9C4',
  evergreen: '#00A651',
  wce: '#87189D',
  seabus: '#F4831F',
  r1: '#008522', r2: '#008522', r3: '#008522', r4: '#008522', r5: '#008522', r6: '#008522',
};

// Province-wide transit modes use one visual language. Individual operator
// route colours are intentionally ignored so the map reads consistently when
// a viewport crosses system boundaries.
export const TRANSIT_MODE_COLORS = {
  train: '#2563EB',
  bus: '#16834A',
  ferry: '#E97817',
} as const;

// Decorative base-style labels hidden to keep listing pins legible. Settlement
// layers deliberately remain visible: the custom municipality source only
// covers Metro Vancouver, while Mapbox place labels provide province-wide city
// and community names (including Kitimat).
export const HIDDEN_LABELS = [
  'poi-label', 'natural-point-label', 'natural-line-label',
  'continent-label', 'state-label', 'country-label',
];

// ── Static paint expressions ──
/** Parks overlay: the base style's `landuse` fill, recoloured while Parks is on. */
export const parksLanduseFillColor = (isDark: boolean): ExpressionSpecification => ['match', ['get', 'class'],
  'park', isDark ? 'rgba(73,217,163,0.35)' : 'rgba(73,217,163,0.4)',
  'pitch', isDark ? 'rgba(73,217,163,0.2)' : 'rgba(73,217,163,0.25)',
  'transparent',
];

// Hoisted out of JSX so referential identity is stable across renders. Mapbox
// re-compiles shader programs whenever a layer's paint object identity
// changes — inline-defining these inside the render function caused a
// shader recompile on every parent re-render (hover, scroll, anything).

export const SCHOOLS_HEAT_PAINT = {
  // Heat represents access to schools, not an inferred quality score. Give
  // every current BC school equal weight so communities outside Metro
  // Vancouver remain visible even when no Fraser Institute score is available.
  // Available scores are expressed by the individual point colours below.
  'heatmap-weight': 1,
  'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 9, 0.8, 13, 2],
  'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 9, 25, 13, 40],
  'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
    0, 'rgba(0,0,0,0)',
    0.15, 'rgba(244,162,89,0.15)',
    0.35, 'rgba(244,162,89,0.35)',
    0.55, 'rgba(255,140,50,0.50)',
    0.75, 'rgba(255,100,30,0.65)',
    1, 'rgba(255,70,20,0.80)',
  ],
  'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0.9, 13, 0.5],
} as const;

export const SCHOOLS_POINTS_PAINT_BASE = {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 8.5, 2.5, 13, 8],
  // Unscored schools render as a neutral grey pin (coverage without implying quality).
  'circle-color': ['case', ['==', ['get', 'score'], null], '#9AA5B1',
    ['interpolate', ['linear'], ['get', 'score'],
      4, '#E8553A', 6, '#F4A259', 8, '#49D9A3', 10, '#2ECC71',
    ]],
  'circle-stroke-width': 2,
  'circle-opacity': ['interpolate', ['linear'], ['zoom'], 8.5, 0.75, 10, 1],
} as const;


export const DENSITY_HEAT_PAINT = {
  'heatmap-weight': ['interpolate', ['linear'], ['get', 'density'], 1, 0.1, 6, 0.4, 12, 0.7, 18, 1],
  'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 9, 0.7, 13, 2],
  'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 9, 25, 13, 40],
  'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
    0, 'rgba(0,0,0,0)',
    0.15, 'rgba(103,169,207,0.15)',
    0.3, 'rgba(103,169,207,0.3)',
    0.5, 'rgba(28,144,153,0.45)',
    0.7, 'rgba(1,108,89,0.6)',
    0.9, 'rgba(0,68,27,0.75)',
    1, 'rgba(0,40,15,0.85)',
  ],
  'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0.85, 13, 0.5],
} as const;

// Client-side clustering thresholds for the per-pin GeoJSON source. Named so
// data-volume tuning is a one-line gated change; same referential-stability
// rationale as the *_PAINT block.
// Cluster through street zoom so co-located condo units (a tower geocodes all
// its units to one point) render as ONE count bubble instead of overlapping
// pins the map can't separate — the bubble counts reconcile with the results
// list. clusterMaxZoom is just under the map's maxZoom(19) so bubbles persist
// at the deepest browse zoom. minPoints=2 badges even a 2-unit building. The
// merge radius is per screen class (clusterRadiusFor in mapboxPinMetrics):
// pins merge only when their discs would touch.
export const CLUSTER_MAX_ZOOM = 18;
export const CLUSTER_MIN_POINTS = 2;

// Data source disclaimers for overlay pills
export const OVERLAY_INFO: Record<string, string> = {
  schools: 'The heat layer shows access to current public and independent schools across British Columbia. Pin colours show available Fraser Institute scores; unrated schools use a neutral pin.',
  density: 'Population density from Statistics Canada 2021 Census, normalized per km². Sub-municipal distribution estimated from census tract data.',
};
